
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useScrollTracking } from "@/hooks/useScrollTracking";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { DateRange } from "react-day-picker";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { useIsMobile } from "@/hooks/use-mobile";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadFormModal from "@/components/LeadGenerationForm/LeadFormModal";
import { Send, Phone, Mail, Menu, Compass, MapPin, CalendarIcon, DollarSign, ArrowLeft, Lock, CheckCircle, Linkedin } from "lucide-react";

const EventIdeas = () => {
  // Tracking hooks
  usePageTracking();
  useScrollTracking();
  
  // ====================== DATA ======================
  // Data for Unique Offsites section
  const uniqueOffsites = [{
    name: "Coucoo Cabanes",
    location: "France",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Coucoo%20Cabanes.jpg"
  }, {
    name: "Amangiri",
    location: "États-Unis",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Utah.jpg"
  }, {
    name: "The Standard",
    location: "Royaume-Uni",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Standard.jpg"
  }, {
    name: "Badrutt's Palace Hotel",
    location: "Suisse",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/154913185.jpg"
  }, {
    name: "Hotel Arts",
    location: "Espagne",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Hotel%20Arts%20Barcelona.jpg"
  }, {
    name: "Monastero Santa Rosa",
    location: "Italie",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Monastero%20Santa%20Rosa.jpg"
  }, {
    name: "Canaves Oia Suites",
    location: "Grèce",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Canaves%20Oia%20Suites.avif"
  }, {
    name: "Six Senses Douro Valley",
    location: "Portugal",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Six%20Senses%20Douro%20Valley.jpg"
  }];

  // Data for Explore Curated Locations section
  const curatedLocations = [{
    title: "Séminaires en bord de mer",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Beach.avif"
  }, {
    title: "Séminaires en montagne",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Mountain.avif"
  }, {
    title: "Séminaires urbains",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Urban.avif"
  }, {
    title: "Séminaires de ski",
    image: "https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/SKI.webp"
  }];

  // Data for Choose Your Event Type section
  const eventTypes = [{
    title: "Séminaires",
    featured: true,
    image: "https://www.aus-visa.org/blog/wp-content/uploads/2024/01/meetup.jpg"
  }, {
    title: "Conférences",
    image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.1.0"
  }, {
    title: "Voyages d'incentive",
    image: "https://images.unsplash.com/photo-1741789596775-a119aa9b7e45?q=80&w=3174&auto=format&fit=crop&ixlib=rb-4.1.0"
  }, {
    title: "Événements clients",
    image: "https://images.unsplash.com/photo-1561065270-5c2af775b542?q=80&w=2736&auto=format&fit=crop&ixlib=rb-4.1.0"
  }, {
    title: "Lancements commerciaux",
    image: "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?q=80&w=3000&auto=format&fit=crop&ixlib=rb-4.1.0"
  }, {
    title: "Événements d'une journée",
    image: "https://images.unsplash.com/photo-1528605248644-14dd04022da1?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.1.0"
  }, {
    title: "Fêtes d'entreprise",
    image: "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?q=80&w=2940&auto=format&fit=crop&ixlib=rb-4.1.0"
  }, {
    title: "Activités team building",
    image: "https://images.unsplash.com/photo-1544928147-79a2dbc1f389?q=80&w=3087&auto=format&fit=crop&ixlib=rb-4.1.0"
  }];

  // ================= FORM TYPES ===================
  // Form data types
  type EventDetailsFormData = {
    eventType: string;
    startDate: string;
    endDate: string;
    participantsCount: string;
    budget: string;
    location: string;
  };
  type ContactInfoFormData = {
    workEmail: string;
    phoneNumber?: string;
  };
  type LeadFormData = EventDetailsFormData & ContactInfoFormData;

  // ============== MOBILE DETECTION ===============
  // Using the imported useIsMobile hook instead of defining it inline
  const isMobile = useIsMobile();

  // ============= LEAD FORM MODAL HOOK =============
  function useLeadFormModal() {
    const [isOpen, setIsOpen] = useState(false);
    const [formTrigger, setFormTrigger] = useState({ location: "unknown", text: "unknown" });
    const openForm = (triggerLocation: string = "unknown", triggerText: string = "unknown") => {
      setFormTrigger({ location: triggerLocation, text: triggerText });
      setIsOpen(true);
    };
    const closeForm = () => setIsOpen(false);
    return {
      isOpen,
      openForm,
      closeForm,
      formTrigger
    };
  }

  // ============= LEAD FORM HOOK =============
  function useLeadForm() {
    // Initial form values
    const initialEventDetails: EventDetailsFormData = {
      eventType: "",
      startDate: "",
      endDate: "",
      participantsCount: "",
      budget: "",
      location: ""
    };
    const initialContactInfo: ContactInfoFormData = {
      workEmail: "",
      phoneNumber: ""
    };
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [eventDetails, setEventDetails] = useState<EventDetailsFormData>(initialEventDetails);
    const [contactInfo, setContactInfo] = useState<ContactInfoFormData>(initialContactInfo);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reset form when modal is closed
    const resetForm = () => {
      setCurrentStep(1);
      setEventDetails(initialEventDetails);
      setContactInfo(initialContactInfo);
    };

    // Move to next step
    const handleNextStep = (data: EventDetailsFormData) => {
      setEventDetails(data);
      setCurrentStep(2);
    };

    // Move to previous step
    const handlePreviousStep = () => {
      setCurrentStep(1);
    };

    // Submit form to Supabase
    const handleSubmit = async (contactData: ContactInfoFormData) => {
      setIsSubmitting(true);
      setContactInfo(contactData);
      try {
        // Log the form data to console
        console.log("Form data to submit:", {
          eventDetails,
          contactInfo: contactData
        });

        // Prepare the data for the alert-form-fr table
        const leadData = {
          id: uuidv4(),
          eventtype: eventDetails.eventType,
          startdate: eventDetails.startDate,
          enddate: eventDetails.endDate,
          participantscount: eventDetails.participantsCount,
          budget: eventDetails.budget, // Keep as string, don't convert to number
          location: eventDetails.location,
          workemail: contactData.workEmail,
          created_at: new Date().toISOString()
        };
        console.log("Formatted data to submit:", leadData);

        // Insert the data into the alert-form-fr table
        const {
          data,
          error
        } = await supabase.from('alert-form-fr').insert([leadData]);
        if (error) {
          console.error("Form submission error details:", error);
          throw error;
        }
        console.log("Submission successful:", data);

        // Show success step
        setCurrentStep(3);
        toast.success("Votre demande a été envoyée avec succès !");
      } catch (error) {
        console.error("Form submission error:", error);
        toast.error("Une erreur est survenue lors de l'envoi de votre demande. Veuillez réessayer.");
      } finally {
        setIsSubmitting(false);
      }
    };
    return {
      currentStep,
      eventDetails,
      contactInfo,
      isSubmitting,
      resetForm,
      handleNextStep,
      handlePreviousStep,
      handleSubmit
    };
  }

  // ============= COMPONENT INITIALIZATION =============
  const {
    isOpen,
    openForm,
    closeForm,
    formTrigger
  } = useLeadFormModal();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // ============= SECTION COMPONENTS =============
  // CuratedLocationsSection component
  const CuratedLocationsSection = ({ locations, onOpenForm }) => {
    return (
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl text-center mb-12 sf-font">
            Explorez des lieux soigneusement sélectionnés
          </h2>
          
          {isMobile ? (
            <div className="relative">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent>
                  {locations.map((location, index) => (
                    <CarouselItem key={index} className="basis-4/5">
                      <div className="flex flex-col">
                        <div className="relative overflow-hidden rounded-lg shadow-md h-56">
                          <img 
                            src={location.image} 
                            alt={location.title}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                          />
                        </div>
                        <h3 className="text-black text-lg font-normal text-center mt-3 px-2">
                          {location.title}
                        </h3>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-2 bg-white/80 hover:bg-white" />
                <CarouselNext className="right-2 bg-white/80 hover:bg-white" />
              </Carousel>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {locations.map((location, index) => (
                <div key={index} className="flex flex-col">
                  <div className="relative overflow-hidden rounded-lg shadow-md h-56">
                    <img 
                      src={location.image} 
                      alt={location.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                  </div>
                  <h3 className="text-black text-lg font-normal text-center mt-3">
                    {location.title}
                  </h3>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-12 text-center">
            <Button onClick={onOpenForm} className="bg-black hover:bg-black/90 text-white rounded-full px-6 py-2">
              <Compass className="h-4 w-4 mr-2" /> Explorez toutes nos destinations
            </Button>
          </div>
        </div>
      </section>
    );
  };

  // EventTypesSection component
  const EventTypesSection = ({ eventTypes, onOpenForm }) => {
    return (
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl text-center mb-12 sf-font">
            Choisissez votre type d'événement
          </h2>
          
          {isMobile ? (
            <div className="relative">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent>
                  {eventTypes.map((type, index) => (
                    <CarouselItem key={index} className="basis-4/5">
                      <div className="flex flex-col">
                        <div className="relative overflow-hidden rounded-lg shadow-md h-56">
                          <img 
                            src={type.image || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2940&auto=format&fit=crop"} 
                            alt={type.title}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                          />
                        </div>
                        <h3 className="text-black text-lg font-normal text-center mt-3 px-2">
                          {type.title}
                        </h3>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-2 bg-white/80 hover:bg-white" />
                <CarouselNext className="right-2 bg-white/80 hover:bg-white" />
              </Carousel>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {eventTypes.map((type, index) => (
                <div 
                  key={index}
                  className={`flex flex-col ${type.featured ? 'sm:col-span-2 lg:col-span-2' : ''}`}
                >
                  <div className="relative overflow-hidden rounded-lg shadow-md h-56">
                    <img 
                      src={type.image || "https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=2940&auto=format&fit=crop"} 
                      alt={type.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                  </div>
                  <h3 className="text-black text-lg font-normal text-center mt-3">
                    {type.title}
                  </h3>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-12 text-center">
            <Button onClick={onOpenForm} className="bg-black hover:bg-black/90 text-white rounded-full px-6 py-2">
              <CalendarIcon className="h-4 w-4 mr-2" /> Planifier votre événement
            </Button>
          </div>
        </div>
      </section>
    );
  };

  // UniqueOffsitesSection component
  const UniqueOffsitesSection = ({ offsites, onOpenForm }) => {
    return (
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-4xl text-center mb-4 sf-font">
            Des lieux uniques pour vos offsites
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Découvrez une sélection de lieux extraordinaires pour vos événements d'entreprise, choisis par nos experts.
          </p>
          
          {isMobile ? (
            <div className="relative">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent>
                  {offsites.map((offsite, index) => (
                    <CarouselItem key={index} className="basis-4/5">
                      <div className="flex flex-col">
                        <div className="relative overflow-hidden rounded-lg shadow-md h-56">
                          <img 
                            src={offsite.image} 
                            alt={offsite.name}
                            className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                          />
                        </div>
                        <div className="mt-3 text-center">
                          <h3 className="text-black text-lg font-normal">
                            {offsite.name}
                          </h3>
                          <p className="text-black/70 flex items-center justify-center mt-1">
                            <MapPin className="h-4 w-4 mr-1" /> {offsite.location}
                          </p>
                        </div>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-2 bg-white/80 hover:bg-white" />
                <CarouselNext className="right-2 bg-white/80 hover:bg-white" />
              </Carousel>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {offsites.map((offsite, index) => (
                <div key={index} className="flex flex-col">
                  <div className="relative overflow-hidden rounded-lg shadow-md h-56">
                    <img 
                      src={offsite.image} 
                      alt={offsite.name}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                  </div>
                  <div className="mt-3 text-center">
                    <h3 className="text-black text-lg font-normal">
                      {offsite.name}
                    </h3>
                    <p className="text-black/70 flex items-center justify-center mt-1">
                      <MapPin className="h-4 w-4 mr-1" /> {offsite.location}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-12 text-center">
            <Button onClick={onOpenForm} className="bg-black hover:bg-black/90 text-white rounded-full px-6 py-2">
              <Send className="h-4 w-4 mr-2" /> Obtenir des propositions personnalisées
            </Button>
          </div>
        </div>
      </section>
    );
  };

  // ============= MAIN PAGE RENDER =============
  return <>
      {/* Use the shared Header component */}
      <Header onOpenForm={() => openForm("event-ideas-header", "Envoyer votre demande")} forceScrolled={true} />
      <LeadFormModal isOpen={isOpen} onClose={closeForm} triggerLocation={formTrigger.location} triggerText={formTrigger.text} />
      
      <main className="pt-20 md:pt-32 pb-10 md:pb-20">
        {/* Hero Section with Creative Event Ideas */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-white via-white/90 to-transparent z-0"></div>
          
          <div className="container mx-auto px-6 py-12 md:py-20 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6 max-w-xl">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-normal sf-font">
                  Élargissez vos horizons avec des idées d'événements créatives
                </h1>
                <h2 className="text-xl md:text-2xl text-gray-700 sf-font mb-6">
                  Nos experts sont là pour vous accompagner
                </h2>
                <Button onClick={() => openForm("event-ideas-hero", "Commencer")} className="bg-black hover:bg-black/90 text-white rounded-full px-6 py-2">
                  <Send className="h-4 w-4 mr-2" /> Commencer
                </Button>
              </div>
              
              <div className="relative h-full flex items-center justify-center">
                <img alt="Inspiration d'idées d'événements créatifs" className="max-w-full h-auto" src="https://21180823.fs1.hubspotusercontent-na1.net/hubfs/21180823/Image%20event%20ideas.png" />
              </div>
            </div>
          </div>
        </section>
        
        {/* Explore Curated Locations Section */}
        <CuratedLocationsSection locations={curatedLocations} onOpenForm={() => openForm("event-ideas-curated-locations", "Explorez toutes nos destinations")} />
        
        {/* Choose Your Event Type Section */}
        <EventTypesSection eventTypes={eventTypes} onOpenForm={() => openForm("event-ideas-event-types", "Planifier votre événement")} />
        
        {/* Unique Offsites Section - Now with CTA button */}
        <UniqueOffsitesSection offsites={uniqueOffsites} onOpenForm={() => openForm("event-ideas-unique-offsites", "Découvrir nos lieux")} />
      </main>
      
      {/* Use the shared Footer component */}
      <Footer />
    </>;
};

export default EventIdeas;
